import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCloudRain, FiArrowLeft, FiList, FiAlignJustify } from 'react-icons/fi';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { useForecastDashboard } from './hooks/useDashboardData';
import DashboardPageHeader from './components/DashboardPageHeader';
import OperationsNav from './components/OperationsNav';
import DashboardFilters from './components/DashboardFilters';
import ForecastTable from './components/ForecastTable';
import ForecastListPagination from './components/ForecastListPagination';
import { FORECAST_TABLE_MAX_ROWS } from './utils/dashboardConstants';
import 'bootstrap/dist/css/bootstrap.css';
import './operations-dashboard.css';

const DENSITY_STORAGE_KEY = 'ops.forecast.density';

const DensityToggle = ({ value, onChange }) => (
    <div className="ops-segmented" role="radiogroup" aria-label="Densité du tableau">
        {[
            { key: 'compact', label: 'Compact', icon: FiAlignJustify },
            { key: 'comfortable', label: 'Confortable', icon: FiList },
        ].map(({ key, label, icon: Icon }) => (
            <button
                key={key}
                type="button"
                role="radio"
                aria-checked={value === key}
                className={`ops-segmented__item${value === key ? ' is-active' : ''}`}
                onClick={() => onChange(key)}
            >
                <Icon aria-hidden="true" />
                {label}
            </button>
        ))}
    </div>
);

const ForecastDashboard = () => {
    const { t } = useTranslation();
    const { filters, setFilter, setFilters, resetFilters, hasActiveFilters } = useDashboardFilters();
    const [page, setPage] = useState(1);
    const pageSize = FORECAST_TABLE_MAX_ROWS;
    const filtersKey = JSON.stringify(filters);
    const [appliedFiltersKey, setAppliedFiltersKey] = useState(filtersKey);

    if (filtersKey !== appliedFiltersKey) {
        setAppliedFiltersKey(filtersKey);
        if (page !== 1) setPage(1);
    }
    const effectivePage = filtersKey !== appliedFiltersKey ? 1 : page;

    const queryFilters = useMemo(() => ({
        ...filters,
        skip: (effectivePage - 1) * pageSize,
        limit: pageSize,
    }), [filters, effectivePage, pageSize]);

    const { data, loading, error, refetch, isError } = useForecastDashboard(queryFilters);
    const [density, setDensity] = useState(() => {
        try {
            return localStorage.getItem(DENSITY_STORAGE_KEY) || 'compact';
        } catch {
            return 'compact';
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(DENSITY_STORAGE_KEY, density);
        } catch {
            // stockage indisponible : préférence non persistée
        }
    }, [density]);

    const items = (data?.items || []).slice(0, pageSize);
    const total = typeof data?.total === 'number' ? data.total : items.length;
    const responseLimit = Math.min(data?.limit ?? pageSize, pageSize);
    const responseSkip = data?.skip ?? (effectivePage - 1) * pageSize;
    const totalPages = data?.total_pages ?? Math.max(1, Math.ceil(total / Math.max(1, responseLimit)));
    const initialLoading = loading && !data;
    const refreshing = loading && Boolean(data);

    useEffect(() => {
        const hasServerTotal = typeof data?.total_pages === 'number' || typeof data?.total === 'number';
        if (hasServerTotal && totalPages > 0 && page > totalPages) setPage(1);
    }, [data, totalPages, page]);

    const handlePageChange = (nextPage) => {
        setPage(Math.max(1, nextPage));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="dashboard-page ops-page">
            <DashboardPageHeader
                title={t('pages.operationsDashboard.forecastTitle')}
                icon={FiCloudRain}
                referenceDate={data?.reference_date}
                metadata={data?.metadata}
                onRefresh={refetch}
                refreshing={loading}
                breadcrumb={[
                    { label: 'Opérations', to: '/dashboard/operations' },
                    { label: 'Forecast' },
                ]}
            />

            <OperationsNav className="mb-3" />

            <DashboardFilters
                filters={filters}
                onFilterChange={setFilter}
                onReset={resetFilters}
                onApply={setFilters}
                hasActiveFilters={hasActiveFilters}
                resultCount={initialLoading || (isError && !data) ? undefined : total}
                resultLabel="forecast"
                visibleFilters={[
                    'run_date',
                    'policy_code',
                    'reservation_number',
                    'partner_id',
                    'company_name',
                    'include_completed',
                    'forecast_timing_hour',
                    'match_current_hour',
                ]}
            />

            <section className="ops-panel" aria-labelledby="ops-forecast-table-title">
                <div className="ops-panel__toolbar">
                    <h2 id="ops-forecast-table-title" className="ops-panel__title">
                        Forecasts
                        {!initialLoading && !(isError && !data) && (
                            <span className="ops-count-pill ops-count-pill--neutral">{total.toLocaleString('fr-FR')}</span>
                        )}
                    </h2>
                    <DensityToggle value={density} onChange={setDensity} />
                </div>

                {isError && data && (
                    <div className="ops-inline-alert" role="alert">
                        Échec de l’actualisation : {error}. Les données affichées peuvent être obsolètes.
                        <button type="button" className="ops-link-btn" onClick={refetch}>Réessayer</button>
                    </div>
                )}

                <ForecastTable
                    items={items}
                    loading={initialLoading}
                    refreshing={refreshing}
                    density={density}
                    filtered={hasActiveFilters}
                    onResetFilters={resetFilters}
                    error={isError && !data ? error : null}
                    onRetry={refetch}
                    fitContent
                    skeletonRows={pageSize}
                />

                {!initialLoading && items.length > 0 && (
                    <ForecastListPagination
                        page={loading ? effectivePage : (data?.page ?? effectivePage)}
                        totalPages={totalPages}
                        total={total}
                        skip={loading ? (effectivePage - 1) * pageSize : responseSkip}
                        limit={responseLimit}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        disabled={loading}
                    />
                )}
            </section>

            <Link to="/dashboard/operations" className="ops-back-link">
                <FiArrowLeft aria-hidden="true" /> Retour au tableau de bord
            </Link>
        </div>
    );
};

export default ForecastDashboard;
